import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

const RELOAD_MARKER_KEY = 'todo:dynamic-import-reload-attempt';
const RELOAD_ATTEMPT_TTL_MS = 30_000;

function isDynamicImportError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error || '');
  return (
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('Importing a module script failed') ||
    message.includes('Loading chunk') ||
    message.includes('ChunkLoadError')
  );
}

function getLastReloadAttempt(): number {
  try {
    return Number(window.sessionStorage.getItem(RELOAD_MARKER_KEY) || 0);
  } catch {
    return 0;
  }
}

function markReloadAttempt(timestamp: number): void {
  try {
    window.sessionStorage.setItem(RELOAD_MARKER_KEY, String(timestamp));
  } catch {
    // Reloading is still useful even if storage is unavailable.
  }
}

function clearReloadAttempt(): void {
  try {
    window.sessionStorage.removeItem(RELOAD_MARKER_KEY);
  } catch {
    // Ignore storage failures.
  }
}

export function lazyWithReload<TProps>(
  loader: () => Promise<{ default: ComponentType<TProps> }>,
): LazyExoticComponent<ComponentType<TProps>> {
  return lazy(async () => {
    try {
      const module = await loader();
      clearReloadAttempt();
      return module;
    } catch (error) {
      if (typeof window !== 'undefined' && isDynamicImportError(error)) {
        const now = Date.now();
        const lastAttempt = getLastReloadAttempt();

        if (now - lastAttempt > RELOAD_ATTEMPT_TTL_MS) {
          markReloadAttempt(now);
          window.location.reload();
          return new Promise<never>(() => {});
        }
      }

      throw error;
    }
  });
}
